import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import { FiClock, FiInfo } from 'react-icons/fi';
import { Map, Marker, TileLayer } from 'react-leaflet';

import mapIcon from 'utils/mapIcon';

import colors from 'styles/colors';
import { Container } from './styles';

import { Sidebar } from '../../components';

import api from '../../services/api';

interface Orphanage {
  name: string;
  latitude: number;
  longitude: number;
  about: string;
  phone: string;
  instructions: string;
  opening_hours: string;
  open_on_weekends: boolean;
  images: {
    id: string;
    url: string;
  }[];
}

interface OrphanageParams {
  id: string;
}

const Orphanage: React.FC = () => {
  const params = useParams<OrphanageParams>();
  const [orphanage, setOrphanage] = useState<Orphanage>();
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    api.get(`/orphanages/${params.id}`).then((response) => {
      const { data } = response;
      setOrphanage(data);
    });
  }, [params.id]);

  function sendWhatsApp(number: string, name: string) {
    const desiredNumber = number.replace(/[^\w]/gi, '');

    const win = window.open(
      `https://wa.me/55${desiredNumber}?text=Olá ${name}, gostaria de agendar uma visita :)`,
      '_blank',
    );

    if (win) {
      win.focus();
    }
  }

  if (!orphanage) {
    return (
      <>
        <p>Carregando...</p>
      </>
    );
  }

  return (
    <Container id="page-orphanage">
      <Sidebar />

      <main>
        <div className="orphanage-details">
          <img
            src={
              orphanage.images[activeImageIndex]?.url ||
              `https://via.placeholder.com/698x300?text=${orphanage.name}`
            }
            alt={orphanage.name}
          />

          <div className="images">
            {orphanage.images.map((image, index) => (
              <button
                key={image.id}
                className={activeImageIndex === index ? 'active' : ''}
                type="button"
                onClick={() => setActiveImageIndex(index)}
              >
                <img src={image.url} alt={orphanage.name} />
              </button>
            ))}
          </div>

          <div className="orphanage-details-content">
            <h1>{orphanage.name}</h1>
            <textarea>{orphanage.about}</textarea>

            <div className="map-container">
              <Map
                center={[orphanage.latitude, orphanage.longitude]}
                zoom={16}
                style={{ width: '100%', height: 280 }}
                dragging={false}
                touchZoom={false}
                zoomControl={false}
                scrollWheelZoom={false}
                doubleClickZoom={false}
              >
                <TileLayer
                  url={`https://api.mapbox.com/styles/v1/mapbox/light-v10/tiles/256/{z}/{x}/{y}@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`}
                />
                <Marker
                  interactive={false}
                  icon={mapIcon}
                  position={[orphanage.latitude, orphanage.longitude]}
                />
              </Map>

              <footer>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${orphanage.latitude},${orphanage.longitude}`}
                >
                  Ver rotas no Google Maps
                </a>
              </footer>
            </div>

            <hr />

            <h2>Instruções para visita</h2>
            <textarea>{orphanage.instructions}</textarea>

            <div className="open-details">
              <div className="hour">
                <FiClock size={32} color="#15B6D6" />
                <p>Segunda à Sexta</p>
                {orphanage.opening_hours}
              </div>
              {orphanage.open_on_weekends ? (
                <div className="open-on-weekends">
                  <FiInfo size={32} color="#39CC83" />
                  <p>É permitido visita aos fins de semana.</p>
                </div>
              ) : (
                <div className="open-on-weekends open-on-weekends--red">
                  <FiInfo size={32} color={colors.danger} />
                  <p>Não é permitido visita aos fins de semana.</p>
                </div>
              )}
            </div>

            <button
              type="button"
              className="contact-button"
              onClick={() => sendWhatsApp(orphanage.phone, orphanage.name)}
            >
              <FaWhatsapp size={20} color="#FFF" />
              Entrar em contato
            </button>
          </div>
        </div>
      </main>
    </Container>
  );
};

export default Orphanage;
